import * as React from "react"
import { Route, Switch, useRouteMatch } from "react-router-dom"
import { LoggedRedirect } from "@digits-shared/components/Router/LoggedRedirect"
import { ProtectedRoute } from "@digits-shared/components/Router/ProtectedRoute"
import useSession from "@digits-shared/hooks/useSession"
import styled from "styled-components"
import { ApplicationGrid } from "src/frontend/components/OS/Applications/Home/ApplicationGrid"
import { useErrorStates } from "src/frontend/components/OS/Applications/Home/LauncherErrors"
import { usePerformActivateLegalEntityAndEmployment } from "src/frontend/components/OS/Applications/hooks/useActivateLegalEntity"
import { useLegalEntityRedirects } from "src/frontend/components/OS/Applications/hooks/useLegalEntityRedirects"
import SharedWithMeApplication from "src/frontend/components/OS/Applications/SharedWithMe/SharedWithMeApplication"
import SupportApplication from "src/frontend/components/OS/Applications/Support/SupportApplication"
import { ApplicationPositioning } from "src/frontend/components/OS/Chrome/Desktop/ApplicationPane"
import { NavSidebar } from "src/frontend/components/Shared/NavSidebar/NavSidebar"
import { Desktop } from "src/frontend/components/Shared/Responsive/Desktop"
import { Mobile } from "src/frontend/components/Shared/Responsive/Mobile"
import { useInternalUserSettings } from "src/frontend/hooks/useInternalUserSettings"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"

/*
  STYLES
*/

export const Application = styled.div`
  --application-full-width: calc(100vw - var(--sidebar-content-width) - 48px);
  min-width: var(--application-pane-full-width);

  .desktop & {
    height: 100%;
  }
  .mobile & {
    min-height: calc(var(--viewport-height) - var(--header-height, 0px));
  }
  margin: 0 auto;
  width: var(--application-full-width);
  max-width: 1920px;
  display: flex;
  flex-direction: column;
`

/*
 COMPONENTS
*/

export const ApplicationsChrome: React.FC<React.PropsWithChildren> = ({ children }) => {
  const isOnFirmDashboard = useRouteMatch(routes.firmDashboardRoot.parameterizedPath)

  const { currentLegalEntity, doppelganger, hasAccountingFirmOrganization } =
    useSession<FrontendSession>()
  const { gridOverlayEnabled } = useInternalUserSettings()

  // If the LE is not in a ready state, we will eventually see it via the error
  // state below, but we need to prevent the application content from rendering
  // while that call is loading. This is important because permissioned queries
  // against a pending LE fail.
  const leReady = currentLegalEntity?.hasDashboardAccess(doppelganger)
  const canRender = leReady || (hasAccountingFirmOrganization && isOnFirmDashboard)

  const errorState = useErrorStates()

  const redirect = useLegalEntityRedirects()
  usePerformActivateLegalEntityAndEmployment(!!redirect)
  if (redirect) return redirect

  if (errorState) {
    return <>{errorState}</>
  }

  return (
    <ApplicationPositioning id="app-positioning">
      <Desktop>
        {gridOverlayEnabled && <ApplicationGrid />}
        <NavSidebar />
        <Applications>{canRender && children}</Applications>
      </Desktop>
      <Mobile>
        <Applications>{canRender && children}</Applications>
      </Mobile>
    </ApplicationPositioning>
  )
}

const Applications: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { currentLegalEntity } = useSession<FrontendSession>()

  return (
    <Application>
      <Switch>
        <Route
          path={[
            routes.legalEntity.parameterizedPath,
            routes.organization.parameterizedPath,
            routes.collectFunds.parameterizedPath,
            routes.firmDashboardRoot.parameterizedPath,
          ]}
        >
          {children}
        </Route>

        <Route
          path={routes.sharedWithMe.parameterizedPath}
          render={() => (
            <ProtectedRoute
              redirectTo={routes.login.generate()}
              component={SharedWithMeApplication}
            />
          )}
        />

        {/* Check for Digits employee access are inside SupportApplication */}
        <Route
          path={[
            routes.internalDGRedirect.parameterizedPath,
            routes.internalFeatures.parameterizedPath,
          ]}
          render={() => (
            <ProtectedRoute
              redirectTo={routes.dashboard.generate()}
              component={SupportApplication}
            />
          )}
        />

        {/*
          Redirect if we load any right drawer page that doesn't match an LE route,
          this redirect will make sure the content area is on springboard launcher
        */}
        <Route
          path="*"
          render={() => (
            <LoggedRedirect
              name="LauncherAndApplications-fallThrough"
              to={
                currentLegalEntity
                  ? routes.legalEntityHome.generate({ leSlug: currentLegalEntity.slug })
                  : routes.sharedWithMe.generate()
              }
            />
          )}
        />
      </Switch>
    </Application>
  )
}
